import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { correlation, getExerciseCandidates, getExercisePreview } from "../services/exerciseApi";
import ExerciseCard from "./ExerciseCard";
import ExerciseFilters from "./ExerciseFilters";
import ExercisePreview from "./ExercisePreview";
import SelectedExercises from "./SelectedExercises";
import { isTwoSituationExercise } from "./examStructure";

const EMPTY_FILTERS = { search: "", moduleId: "", lessonId: "", evaluationType: "", difficulty: "" };
const statusMessages = {
  400: "Certains critères sont invalides. Vérifiez votre sélection.",
  401: "Votre session a expiré. Reconnectez-vous.",
  403: "Ce compte n’est pas autorisé à consulter la banque d’exercices.",
  404: "La ressource demandée est introuvable.",
  500: "Le serveur ne peut pas traiter la demande pour le moment.",
};

const readableError = (error, fallback) => {
  if (error.code === "ECONNABORTED") return "Le serveur met trop de temps à répondre. Réessayez.";
  if (!error.response) return "Connexion au serveur impossible. Vérifiez votre accès Internet.";
  const data = error.response.data;
  const detail = typeof data?.detail === "string" ? data.detail : Object.values(data || {}).find((value) => typeof value === "string");
  return detail || statusMessages[error.response.status] || fallback;
};

const typeLabel = (code) => code.replaceAll("_", " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase());

export default function ExerciseBrowser({ context, modules, lessons, difficulties, classLabel, tier, selectedExercises, expectedPositions = [], selectionNotice, generationState, onDismissNotice, onGenerate, onRetryGeneration, onSelect, onRemove, onMove, onDropExercise }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [catalog, setCatalog] = useState({ count: 0, page: 1, pageSize: 20, totalPages: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [correlationData, setCorrelationData] = useState(null);
  const [correlationError, setCorrelationError] = useState(null);
  const [preview, setPreview] = useState({ exercise: null, url: null, loading: false, error: null });
  const requestSequence = useRef(0);
  const previewUrl = useRef(null);
  const selectedIds = useMemo(() => new Set(selectedExercises.map((item) => item.id)), [selectedExercises]);
  const moduleIds = filters.moduleId ? [Number(filters.moduleId)] : context.moduleIds;
  const lessonIds = filters.lessonId ? [Number(filters.lessonId)] : context.lessonIds;
  const moduleKey = moduleIds.join(",");
  const lessonKey = lessonIds.join(",");

  const loadCandidates = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const result = await getExerciseCandidates({
        matiere: context.matiere,
        classe: context.classe,
        session: context.session,
        modules: moduleKey || undefined,
        lessons: lessonKey || undefined,
        difficulty: filters.difficulty || undefined,
        evaluation_type: filters.evaluationType || undefined,
        search: filters.search.trim() || undefined,
        page,
        page_size: 20,
      });
      if (sequence === requestSequence.current) setCatalog(result);
    } catch (requestError) {
      if (sequence === requestSequence.current) {
        setCatalog({ count: 0, page, pageSize: 20, totalPages: 0, results: [] });
        setError(readableError(requestError, "Impossible de charger les exercices."));
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [context.classe, context.matiere, context.session, filters.difficulty, filters.evaluationType, filters.search, lessonKey, moduleKey, page]);

  useEffect(() => {
    const timer = window.setTimeout(loadCandidates, filters.search ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [filters.search, loadCandidates]);

  const loadCorrelation = useCallback(async () => {
    setCorrelationError(null);
    try {
      const selectedComp1 = selectedExercises.find((item) => item.positionKey === "phy_comp1");
      const requiredPositions = expectedPositions.filter(
        (item) => !(item.key === "phy_comp2" && isTwoSituationExercise(selectedComp1)),
      );
      setCorrelationData(await correlation({
        matiere: context.matiere,
        classe: context.classe,
        session: context.session,
        modules: context.moduleIds,
        lessons: context.lessonIds,
        difficulty: filters.difficulty,
        sections: requiredPositions.map((item) => ({
          key: item.key,
          evaluation_type: item.evaluation_type,
          difficulty: "",
        })),
      }));
    } catch (requestError) {
      setCorrelationData(null);
      setCorrelationError(readableError(requestError, "Impossible d’analyser la couverture."));
    }
  }, [context.classe, context.lessonIds, context.matiere, context.moduleIds, context.session, expectedPositions, filters.difficulty, selectedExercises]);

  useEffect(() => { loadCorrelation(); }, [loadCorrelation]);
  useEffect(() => { setPage(1); }, [context.classe, context.matiere, context.session, filters.difficulty, filters.evaluationType, filters.lessonId, filters.moduleId, filters.search]);
  useEffect(() => () => { if (previewUrl.current) URL.revokeObjectURL(previewUrl.current); }, []);

  const evaluationTypes = useMemo(() => {
    const codes = new Set((correlationData?.sections || []).map((item) => item.evaluation_type).filter(Boolean));
    catalog.results.forEach((item) => { if (item.evaluation_type) codes.add(item.evaluation_type); });
    return [...codes].map((code) => ({ code, label: typeLabel(code) }));
  }, [catalog.results, correlationData]);
  const stats = useMemo(() => ({
    count: selectedExercises.length,
    points: selectedExercises.reduce((total, item) => total + (Number(item.points) || 0), 0),
    withoutPoints: selectedExercises.filter((item) => item.points == null).length,
  }), [selectedExercises]);
  const requiredTypes = useMemo(() => new Set((correlationData?.sections || []).map((item) => item.evaluation_type).filter(Boolean)), [correlationData]);
  const selectedTypes = useMemo(() => new Set(selectedExercises.map((item) => item.evaluation_type).filter(Boolean)), [selectedExercises]);
  const coversRequiredTypes = [...requiredTypes].every((type) => selectedTypes.has(type));
  const selectedPositions = useMemo(() => new Set(selectedExercises.map((item) => item.positionKey).filter(Boolean)), [selectedExercises]);
  const comp1 = selectedExercises.find((item) => item.positionKey === "phy_comp1");
  const effectivePositions = expectedPositions.filter((item) => !(item.key === "phy_comp2" && isTwoSituationExercise(comp1)));
  const coversExpectedPositions = effectivePositions.length > 0 && effectivePositions.every((item) => selectedPositions.has(item.key));
  const canGenerate = Boolean(tier && selectedExercises.length && correlationData?.status === "green" && requiredTypes.size && coversRequiredTypes && coversExpectedPositions && generationState.status !== "submitting");
  const generationReason = !tier
    ? "Choisissez d’abord un palier."
    : !selectedExercises.length
      ? "Sélectionnez les exercices requis."
      : correlationData?.status !== "green"
        ? "Une couverture verte est requise avant la génération."
        : !coversRequiredTypes
          ? "Ajoutez au moins un exercice pour chaque section pédagogique requise."
          : !coversExpectedPositions
            ? "Remplissez toutes les positions prévues par les règles du site."
          : "La génération est momentanément indisponible.";
  const toggle = (exercise) => selectedIds.has(exercise.id) ? onRemove(exercise.id) : onSelect(exercise);

  const closePreview = () => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setPreview({ exercise: null, url: null, loading: false, error: null });
  };
  const openPreview = async (exercise) => {
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = null;
    setPreview({ exercise, url: null, loading: true, error: null });
    try {
      const blob = await getExercisePreview(exercise.id);
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      previewUrl.current = url;
      setPreview({ exercise, url, loading: false, error: null });
    } catch (requestError) {
      setPreview({ exercise, url: null, loading: false, error: readableError(requestError, "Impossible d’afficher cet aperçu PDF.") });
    }
  };

  return (
    <section id="exam-exercise-catalog" className="ql-exercise-browser" tabIndex="-1">
      <div className="ql-exam-card-heading"><span className="ql-teacher-eyebrow">Étape 3</span><h2>Sélection intelligente des exercices</h2><p>Les résultats proviennent de la banque pédagogique Quick Learning et respectent les critères du compte enseignant.</p></div>
      {selectionNotice && <div className="ql-exam-inline-state warning" role="status"><span>{selectionNotice}</span><button type="button" onClick={onDismissNotice}>Fermer</button></div>}
      <ExerciseFilters filters={filters} modules={modules} lessons={lessons} difficulties={difficulties} evaluationTypes={evaluationTypes} classLabel={classLabel} onChange={(key, value) => setFilters((current) => ({ ...current, [key]: value }))} onReset={() => setFilters(EMPTY_FILTERS)} />
      <div className={`ql-correlation-panel ${correlationData?.status || "neutral"}`}>
        <div><span className="ql-correlation-dot"></span><strong>Couverture de la banque</strong></div>
        {correlationError ? <p>{correlationError} <button type="button" onClick={loadCorrelation}>Réessayer</button></p> : correlationData ? <><div className="ql-correlation-values"><span><strong>{correlationData.total_candidates}</strong> candidats</span><span><strong>{correlationData.coverage_percent}%</strong> de couverture</span><span><strong>{correlationData.covered_sections}/{correlationData.total_sections}</strong> sections couvertes</span></div>{correlationData.sections?.length > 0 && <div className="ql-correlation-sections">{correlationData.sections.map((section) => <span key={section.key} className={section.covered ? "covered" : "missing"}>{section.key} · {section.candidate_count}</span>)}</div>}{correlationData.warnings?.length > 0 && <ul>{correlationData.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>}</> : <p>Analyse de la couverture…</p>}
      </div>
      <div className="ql-exercise-layout">
        <div className="ql-exercise-results">
          <div className="ql-exercise-results-heading"><h2>Exercices disponibles</h2><span>{catalog.count} résultat{catalog.count !== 1 ? "s" : ""}</span></div>
          {loading && <div className="ql-exam-inline-state" role="status"><span className="spinner-border spinner-border-sm"></span>Chargement des exercices…</div>}
          {error && <div className="ql-exam-inline-state error" role="alert"><span>{error}</span><button type="button" onClick={loadCandidates}>Réessayer</button></div>}
          {!loading && !error && !catalog.results.length && <div className="ql-exam-empty"><p>Aucun exercice ne correspond aux critères sélectionnés.</p></div>}
          {!loading && !error && <div className="ql-exercise-list">{catalog.results.map((exercise) => <ExerciseCard key={exercise.id} exercise={exercise} selected={selectedIds.has(exercise.id)} onPreview={openPreview} onToggle={toggle} />)}</div>}
          {!loading && !error && catalog.totalPages > 1 && <nav className="ql-exercise-pagination" aria-label="Pagination des exercices"><button type="button" disabled={catalog.page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><i className="bi bi-chevron-left"></i> Précédent</button><span>Page {catalog.page} sur {catalog.totalPages}</span><button type="button" disabled={catalog.page >= catalog.totalPages} onClick={() => setPage((current) => current + 1)}>Suivant <i className="bi bi-chevron-right"></i></button></nav>}
        </div>
        <SelectedExercises exercises={selectedExercises} expectedPositions={effectivePositions} stats={stats} canGenerate={canGenerate} generationReason={generationReason} generationError={generationState.error} retryAvailable={generationState.retryAvailable} generating={generationState.status === "submitting"} onGenerate={onGenerate} onRetryGeneration={onRetryGeneration} onRemove={onRemove} onMove={onMove} onDropExercise={onDropExercise} />
      </div>
      <ExercisePreview exercise={preview.exercise} url={preview.url} loading={preview.loading} error={preview.error} selected={preview.exercise ? selectedIds.has(preview.exercise.id) : false} onClose={closePreview} onToggle={toggle} onRetry={() => openPreview(preview.exercise)} />
    </section>
  );
}
